import React, { useState, useMemo, useRef, useEffect } from 'react'
import { VerticalFlex } from '@eversports/react-components/primitives/Flex'
import TextField from '@eversports/react-components/primitives/TextField'
import Text from '@eversports/react-components/primitives/Text'
import { usePyroField, PyroFormValues } from 'pyro-form'
import useDebounce from '@eversports/react-components/logical/useDebounce'
import styled from 'styled-components'
import { useIntl } from '../../localization/react'

interface Sport {
  id: string
  name: string
}

interface SearchableSportsSelectProps<Values extends PyroFormValues> {
  name: Extract<keyof Values, string>
  label: string
  sports: Array<Sport>
  fullWidth?: boolean
}

const Container = styled.div`
  position: relative;
  width: 100%;
`

const SearchInput = styled(TextField)`
  margin-bottom: 0;
`

const DropdownContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const SportOption = styled.div<{ isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  background: ${(props) => (props.isSelected ? '#f0f0f0' : 'white')};
  border-bottom: 1px solid #eee;

  &:hover {
    background: #f5f5f5;
  }

  &:last-child {
    border-bottom: none;
  }
`

const SelectedSportsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
  margin-bottom: 8px;
`

const NoResultsText = styled.div`
  padding: 12px 16px;
  color: #666;
  font-style: italic;
`

const ChipContainer = styled.div`
  display: inline-flex;
  align-items: center;
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 16px;
  padding: 4px 12px;
  font-size: 14px;
  color: #1976d2;
`

const ChipDeleteButton = styled.button`
  background: none;
  border: none;
  margin-left: 8px;
  cursor: pointer;
  color: #1976d2;
  font-size: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #0d47a1;
  }
`

function SearchableSportsSelect<Values extends PyroFormValues>({
  name,
  label,
  sports,
  fullWidth = true,
}: SearchableSportsSelectProps<Values>) {
  const [searchTerm, setSearchTerm] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const intl = useIntl()

  const { core } = usePyroField<Values, Extract<keyof Values, string>>(name)
  const selectedSportIds = core.value
  const onChange = core.onChange

  // Debounce search term to avoid excessive filtering
  const debouncedSearchTerm = useDebounce(searchTerm, 300)

  // Filter sports based on debounced search term
  const filteredSports = useMemo(() => {
    if (!debouncedSearchTerm.trim()) {
      return sports.slice(0, 20) // Show first 20 sports when no search
    }

    return sports.filter((sport) => sport.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase())).slice(0, 20) // Limit to 20 results
  }, [sports, debouncedSearchTerm])

  // Get selected sports objects
  const selectedSports = useMemo(() => {
    const ids = Array.isArray(selectedSportIds) ? selectedSportIds : []
    return sports.filter((sport) => ids.includes(sport.id))
  }, [sports, selectedSportIds])

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setIsOpen(true)
  }

  const handleSportSelect = (sport: Sport) => {
    const currentIds = Array.isArray(selectedSportIds) ? selectedSportIds : []

    if (currentIds.includes(sport.id)) {
      // Remove sport if already selected
      onChange(currentIds.filter((id) => id !== sport.id))
    } else {
      // Add sport if not selected
      onChange([...currentIds, sport.id])
    }
  }

  const handleRemoveSport = (sportId: string) => {
    const currentIds = Array.isArray(selectedSportIds) ? selectedSportIds : []
    onChange(currentIds.filter((id) => id !== sportId))
  }

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  const isSelected = (sportId: string) => {
    const currentIds = Array.isArray(selectedSportIds) ? selectedSportIds : []
    return currentIds.includes(sportId)
  }

  return (
    <VerticalFlex>
      <Text is="label" style={{ marginBottom: 8, fontWeight: 500 }}>
        {label}
      </Text>

      <Container ref={containerRef}>
        <SearchInput
          name="sportSearch"
          placeholder={intl('registration-form-sports-search-placeholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          fullWidth={fullWidth}
        />

        {isOpen && (
          <DropdownContainer>
            {filteredSports.length > 0 ? (
              filteredSports.map((sport) => (
                <SportOption key={sport.id} isSelected={isSelected(sport.id)} onClick={() => handleSportSelect(sport)}>
                  <Text style={{ fontSize: 14 }}>
                    {isSelected(sport.id) ? '✓ ' : ''}
                    {sport.name}
                  </Text>
                </SportOption>
              ))
            ) : (
              <NoResultsText>
                <Text style={{ fontSize: 14 }}>
                  {searchTerm
                    ? intl('registration-form-sports-no-results')
                    : intl('registration-form-sports-start-typing')}
                </Text>
              </NoResultsText>
            )}
          </DropdownContainer>
        )}
      </Container>

      {selectedSports.length > 0 && (
        <SelectedSportsContainer>
          {selectedSports.map((sport) => (
            <ChipContainer key={sport.id}>
              {sport.name}
              <ChipDeleteButton onClick={() => handleRemoveSport(sport.id)}>×</ChipDeleteButton>
            </ChipContainer>
          ))}
        </SelectedSportsContainer>
      )}
    </VerticalFlex>
  )
}

export default SearchableSportsSelect
